import React, { Fragment, useEffect, useRef, useState } from 'react';
import { IResourceMessageFeed } from './interfaces';
import { Feed } from '../Feed';
import {
  useDeleteLikeMutation,
  useGetFeedMessages,
  usePostFeedMesageMutation,
  usePostLikeMutation,
} from '@reactQuery/feeds/feed';
import {
  FeedComment,
  FeedCommentLikeUpdate,
  FeedLikeUpdate,
  FeedMessage,
} from '@api/feeds/interfaces';
import { MessagePost } from '../Feed/components/MessagePost';
import { Loader } from '../Loader';
import { useEcho } from '@laravel/echo-react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { getCurrentUserAsMember } from '@helpers/memberHelper';
import { isDebug } from '@helpers/envHelper';
import './styles.css';
import { mapApiComments } from './helpers';
import { feedCommentsEmitter } from '@events/feedCommentsEvents';
import { EndOfFeed } from '../EndOfFeed';
import { useQueryClient } from '@tanstack/react-query';
import { useBroadcasting } from '@hooks/useBroadcasting';
import { useAuth } from '@context/AuthProvider';

export const ResourceMessageFeed: React.FC<IResourceMessageFeed> = ({
  resourceType,
  resourceId,
  canPostMessages = false,
}): JSX.Element => {
  const { channel } = useBroadcasting();
  const { currentUser } = useAuth();
  const [page, setPage] = useState<number>(0);
  const [messages, setMessages] = useState<FeedMessage[]>([]);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const pendingLikeIds = useRef<Set<number>>(new Set());
  const currentMemberIdRef = useRef<number | undefined>(currentUser?.member?.id);
  const postAuthor = getCurrentUserAsMember();
  const queryClient = useQueryClient();
  const { mutate: postLike } = usePostLikeMutation();
  const { mutate: deleteLike } = useDeleteLikeMutation();

  currentMemberIdRef.current = currentUser?.member?.id;

  const resourceFeedChannel = channel(
    `feed.resource.${resourceType}.${resourceId}`
  );

  const { mutate: postFeedMessage, isPending: isPostingMessage } =
    usePostFeedMesageMutation();

  const { data, isLoading, error } = useGetFeedMessages({
    resourceType,
    resourceId,
    page: page,
    pageSize: 10,
  });

  useEcho<FeedMessage>(
    resourceFeedChannel,
    '.feed.message-posted',
    (newMessage) => {
      if (isDebug()) {
        console.log(`Echo: Adding message: ${newMessage.id}.`);
      }

      setMessages((prev) => {
        return [newMessage, ...prev];
      });
    }
  );

  useEcho<FeedMessage>(
    resourceFeedChannel,
    '.feed.message-deleted',
    (deletedMessage) => {
      if (isDebug()) {
        console.log(`Echo: Deleting message: ${deletedMessage.id}.`);
      }

      setMessages((prev) =>
        prev.filter((message) => message.id !== deletedMessage.id)
      );
    }
  );

  useEcho<FeedComment>(
    resourceFeedChannel,
    '.feed.comment-posted',
    (newComment) => {
      if (isDebug()) {
        console.log(
          `Echo: Adding comment ${newComment.id} on message ${newComment.messageId}.`
        );
      }

      setMessages((prevMessages) =>
        prevMessages.map((message) => {
          if (message.id === newComment.messageId) {
            return {
              ...message,
              comments: [newComment, ...(message.comments || [])],
              commentsCount: message.commentsCount + 1,
            };
          }

          return message;
        })
      );
    }
  );

  useEcho<FeedComment>(
    resourceFeedChannel,
    '.feed.comment-deleted',
    (deletedComment) => {
      if (isDebug()) {
        console.log(`Echo: Deleting comment: ${deletedComment.id}.`);
      }

      setMessages((prevMessages) =>
        prevMessages.map((message) => {
          if (message.id === deletedComment.messageId) {
            return {
              ...message,
              comments: message.comments
                ? message.comments?.filter(
                    (comment) => comment.id !== deletedComment.id
                  )
                : [],
              commentsCount: message.commentsCount - 1,
            };
          }

          return message;
        })
      );

      feedCommentsEmitter.emit('deleted', {
        messageId: deletedComment.messageId,
        commentId: deletedComment.id,
      });
    }
  );

  const applyLikeBroadcast = (update: FeedLikeUpdate, liked: boolean): void => {
    const isCurrentMember = update.memberId === currentMemberIdRef.current;

    setMessages((prevMessages) =>
      prevMessages.map((message) => {
        if (message.id !== update.messageId) {
          return message;
        }

        return {
          ...message,
          likesCount: update.likesCount,
          likedByCurrentUser: isCurrentMember
            ? liked
            : message.likedByCurrentUser,
        };
      })
    );
  };

  useEcho<FeedLikeUpdate>(
    resourceFeedChannel,
    '.feed.like-posted',
    (update) => {
      if (isDebug()) {
        console.log(
          `Echo: Like posted on message ${update.messageId} by member ${update.memberId}.`
        );
      }

      applyLikeBroadcast(update, true);
    }
  );

  useEcho<FeedLikeUpdate>(
    resourceFeedChannel,
    '.feed.like-deleted',
    (update) => {
      if (isDebug()) {
        console.log(
          `Echo: Like deleted on message ${update.messageId} by member ${update.memberId}.`
        );
      }

      applyLikeBroadcast(update, false);
    }
  );

  const applyCommentLikeBroadcast = (
    update: FeedCommentLikeUpdate,
    liked: boolean
  ): void => {
    const isCurrentMember = update.memberId === currentMemberIdRef.current;

    setMessages((prevMessages) =>
      prevMessages.map((message) => {
        if (message.id !== update.messageId) {
          return message;
        }

        return {
          ...message,
          comments: message.comments?.map((comment) => {
            if (comment.id !== update.commentId) {
              return comment;
            }

            return {
              ...comment,
              likesCount: update.likesCount,
              likedByCurrentUser: isCurrentMember
                ? liked
                : comment.likedByCurrentUser,
            };
          }),
        };
      })
    );

    feedCommentsEmitter.emit('likeUpdated', {
      ...update,
      liked,
    });
  };

  useEcho<FeedCommentLikeUpdate>(
    resourceFeedChannel,
    '.feed.comment-like-posted',
    (update) => {
      if (isDebug()) {
        console.log(
          `Echo: Like posted on comment ${update.commentId} of message ${update.messageId}.`
        );
      }

      applyCommentLikeBroadcast(update, true);
    }
  );

  useEcho<FeedCommentLikeUpdate>(
    resourceFeedChannel,
    '.feed.comment-like-deleted',
    (update) => {
      if (isDebug()) {
        console.log(
          `Echo: Like deleted on comment ${update.commentId} of message ${update.messageId}.`
        );
      }

      applyCommentLikeBroadcast(update, false);
    }
  );

  const endOfFeedReached = data && data.meta.currentPage === data.meta.lastPage;

  const onLikeClick = (message: FeedMessage): void => {
    if (pendingLikeIds.current.has(message.id)) {
      return;
    }

    const nextLiked = !message.likedByCurrentUser;
    const previousLiked = message.likedByCurrentUser;
    const previousCount = message.likesCount;

    pendingLikeIds.current.add(message.id);

    setMessages((prevMessages) =>
      prevMessages.map((item) => {
        if (item.id !== message.id) {
          return item;
        }

        return {
          ...item,
          likedByCurrentUser: nextLiked,
          likesCount: Math.max(0, previousCount + (nextLiked ? 1 : -1)),
        };
      })
    );

    const mutation = nextLiked ? postLike : deleteLike;

    mutation(message.id, {
      onError: () => {
        setMessages((prevMessages) =>
          prevMessages.map((item) => {
            if (item.id !== message.id) {
              return item;
            }

            return {
              ...item,
              likedByCurrentUser: previousLiked,
              likesCount: previousCount,
            };
          })
        );
      },
      onSettled: () => {
        pendingLikeIds.current.delete(message.id);
      },
    });
  };

  const onMessagePost = (message: string): void => {
    if (isPostingMessage) {
      return;
    }

    postFeedMessage({
      resourceType,
      resourceId,
      feedMessageParams: {
        body: message,
      },
    });
  };

  useEffect(() => {
    if (data?.data) {
      setMessages((prev) => {
        return [...prev, ...data.data];
      });
    }
  }, [data?.data]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['resource-feed-messages', resourceType, resourceId],
      });
    };
  }, [queryClient]);

  useEffect(() => {
    if (
      !loaderRef.current ||
      data?.meta.currentPage === data?.meta.lastPage ||
      isLoading
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (first.isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [data]);

  return (
    <Fragment>
      <Feed
        postAuthor={postAuthor}
        onMessagePost={onMessagePost}
        sx={{
          '& .messages-wrapper > div': {
            mb: '10px',
          },
        }}
      >
        <TransitionGroup className="messages-wrapper">
          {messages.map((message) => {
            return (
              <CSSTransition
                key={message.id}
                timeout={300}
                classNames="message"
              >
                <MessagePost
                  id={message.id}
                  author={{
                    slug: message.author.slug,
                    name: message.author.fullName,
                  }}
                  date={new Date(message.createdAt)}
                  message={message.body}
                  commentsCount={message.commentsCount}
                  likesCount={message.likesCount}
                  likedByCurrentUser={message.likedByCurrentUser}
                  onLikeClick={() => onLikeClick(message)}
                  intitalComments={
                    message.comments ? mapApiComments(message.comments) : []
                  }
                />
              </CSSTransition>
            );
          })}
        </TransitionGroup>

        {!endOfFeedReached && (
          <div ref={loaderRef} style={{ height: '40px' }}>
            {isLoading && <Loader />}
          </div>
        )}

        {endOfFeedReached && <EndOfFeed />}
      </Feed>
    </Fragment>
  );
};
